// src/store/auth_slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "./store";
import apiClient from "../services/api_client";
import axios from "axios";
import { store } from "./store";

/**
 * Interface representing a user in the system
 */
interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  token: string; // We'll store accessToken here for compatibility
  refreshToken: string;
  creationYear: string;
  phoneNumber: string | null;
  profilePicture: string | null;
  stripeAccountId: string | null;
  stripeOnboardingComplete: boolean;
  roles: string[];
  }
  export interface DecodedToken {
  sub?: string;
  exp?: number;
  iat?: number;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  profilePicture?: string;
  stripeAccountId?: string;
  stripeOnboardingComplete?: boolean;
  roles?: string[];
  id?: number;
}

/**
 * Interface representing the authentication state
 */
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  error: string | null;
  loading: boolean;
}

/**
 * Helper function to extract email from JWT token
 */
function parseJwt(token: string): DecodedToken | null {
  if (!token) {
    return null;
  }
  
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) { return null; }
    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    // Use 'atob' directly available in browsers
    const payload = typeof window !== 'undefined' ? window.atob(base64) : Buffer.from(base64, 'base64').toString();
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

/**
 * Function to determine the initial state from localStorage
 */
const getInitialAuthState = (): AuthState => {
  let user: User | null = null;
  let isAuthenticated = false;

  try {
    const token = localStorage.getItem("auth_token");
    const refreshToken = localStorage.getItem("refresh_token");

    if (token && refreshToken) {
      const decodedToken = parseJwt(token);
      const currentTime = Date.now() / 1000;

      // Check if token exists and is not expired
      if (decodedToken && decodedToken.exp && decodedToken.exp > currentTime) {
        isAuthenticated = true;
        // Reconstruct user object from token (similar to checkAndRestoreSession)
        user = {
          id: decodedToken?.id || 0,
          firstName: decodedToken?.firstName || "",
          lastName: decodedToken?.lastName || "",
          email: decodedToken?.sub || "",
          token,
          refreshToken,
          creationYear: decodedToken?.iat ? new Date(decodedToken.iat * 1000).getFullYear().toString() : new Date().getFullYear().toString(),
          phoneNumber: decodedToken?.phoneNumber || null,
          profilePicture: decodedToken?.profilePicture || null,
          stripeAccountId: decodedToken?.stripeAccountId || null,
          stripeOnboardingComplete: decodedToken?.stripeOnboardingComplete || false,
          roles: decodedToken?.roles || []
        };
      } else {
         // Token exists but is expired or invalid - clear potentially stale tokens
         localStorage.removeItem("auth_token");
         localStorage.removeItem("refresh_token");
      }
    }
  } catch {
      // Ensure logout state if there's an error reading storage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
  }

  return {
    isAuthenticated,
    user,
    error: null, // Start with no error
    loading: false, // Start with no loading
  };
};

/**
 * Initial authentication state determined by localStorage
 */
const initialState: AuthState = getInitialAuthState();

/**
 * Authentication slice containing reducers and actions
 */
const authSlice = createSlice({
  name: "auth",
  initialState, // Use the dynamically determined initial state
  reducers: {
    // Updates authentication state and manages token storage
    setAuthState: (
      state,
      action: PayloadAction<{ isAuthenticated: boolean; user: User | null }>,
    ) => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user;
      // Still manage localStorage on explicit state changes
      if (action.payload.isAuthenticated && action.payload.user) {
        localStorage.setItem("auth_token", action.payload.user.token);
        localStorage.setItem("refresh_token", action.payload.user.refreshToken);
      } else {
        // This case handles explicit logout or auth failure
        localStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
      }
    },
    
    // Updates error state
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Updates loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Handles user logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
    },
    
    // Updates user profile information
    updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

// Export actions
export const {
  setAuthState,
  setAuthError,
  setLoading,
  logout,
  updateUserProfile,
} = authSlice.actions;

// Export selector
export const selectAuth = (state: RootState) => state.auth;

/**
 * Async action to handle user login
 */
export const login = (credentials: { email: string; password: string }) => 
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setAuthError(null)); // Clear previous errors
    try {
      // Assuming apiClient.post returns the data object directly { accessToken, refreshToken, ... }
      const loginResponse = await apiClient.post<{accessToken: string, refreshToken: string, message?: string}>(
        "/auth/login", 
        credentials
      );

      // Explicitly check for accessToken and refreshToken
      if (loginResponse && typeof loginResponse.accessToken === 'string' && typeof loginResponse.refreshToken === 'string') {
        const accessToken = loginResponse.accessToken;
        const refreshToken = loginResponse.refreshToken;
        
        localStorage.setItem("auth_token", accessToken);
        localStorage.setItem("refresh_token", refreshToken); // Also store refresh token
        
        const decodedToken = parseJwt(accessToken);
        const email = decodedToken?.sub || credentials.email;
        
        // Make sure User interface matches expected structure
        const user: User = {
          id: decodedToken?.id || 0, // Attempt to get ID if available in token
          firstName: decodedToken?.firstName || "", // Attempt if available
          lastName: decodedToken?.lastName || "", // Attempt if available
          email: email,
          token: accessToken,
          refreshToken: refreshToken,
          creationYear: decodedToken?.iat ? new Date(decodedToken.iat * 1000).getFullYear().toString() : new Date().getFullYear().toString(),
          phoneNumber: decodedToken?.phoneNumber || null, // Attempt if available
          profilePicture: decodedToken?.profilePicture || null, // Attempt if available
          stripeAccountId: decodedToken?.stripeAccountId || null,
          stripeOnboardingComplete: decodedToken?.stripeOnboardingComplete || false,
          roles: decodedToken?.roles || []
        };
        
        dispatch(setAuthState({ isAuthenticated: true, user }));

        // Immediately fetch full user details after login
        await dispatch(fetchUserDetails());

        // dispatch(setAuthError(null)); // Already cleared at the start
        return { success: true };
      } else {
        // This path indicates the response structure was not as expected
        throw new Error("Invalid login response data");
      }
    } catch (error: unknown) {
      let errorMessage = "Login failed due to an unexpected error.";
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Invalid email or password.";
        } else {
          // Try to get a more specific message from the backend response
          errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            `Server error: ${error.response?.status || 'Unknown'}`;
        }
      } else {
         // Handle non-Axios errors (e.g., the 'Invalid login response data' error)
         if (error instanceof Error) {
           errorMessage = error.message || errorMessage;
         }
      }
      
      dispatch(setAuthError(errorMessage));
      // Ensure loading is set to false even if returning an error object
      dispatch(setLoading(false)); 
      return { error: errorMessage };
    } finally {
      // setLoading(false) is called in the catch block now, but keep it here 
      // as a fallback for non-error paths if any were added later.
      // However, in the current structure, it might be redundant if success path always returns.
      // Consider removing if catch block reliably handles loading state.
       dispatch(setLoading(false));
    }
  };

/**
 * Action to perform user logout
 */
/**
 * Logs out the user both locally and on the backend.
 * NOTE: If you see a CORS error here, it's because the backend /api/auth/logout endpoint
 * is redirecting to a non-CORS-enabled URL (e.g., http://staging.droneden.ro/login?logout).
 * The backend should return a 200 OK (or 204 No Content) with CORS headers, NOT a redirect.
 * The frontend will always clear tokens and Redux state locally, even if the API call fails.
 */
export const performLogout = () => async (dispatch: AppDispatch) => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      // Call logout endpoint (may fail due to CORS/redirect, but that's OK)
      await apiClient.post("/auth/logout", { refreshToken });
    }
  } catch {
    // This error is expected if the backend does not handle CORS/redirects properly.
  } finally {
    // Always clear local state and tokens, regardless of API call result.
    dispatch(logout());
  }
};

/**
 * Action to check and restore user session
 */
export const checkAndRestoreSession = () => async (dispatch: AppDispatch) => {
  const token = localStorage.getItem("auth_token");
  const refreshToken = localStorage.getItem("refresh_token");
  
  if (!token || !refreshToken) {
    dispatch(setAuthState({ isAuthenticated: false, user: null }));
    return false;
  }

  try {
    // Attempt to validate the token by decoding it
    const decodedToken = parseJwt(token);
    
    // Check if token is expired
    const currentTime = Date.now() / 1000;
    if (decodedToken && decodedToken.exp && decodedToken.exp > currentTime) {
      // Token is still valid
      const email = decodedToken.sub || "";
      
      // Create minimal user object
      const userData: User = {
        id: 0,
        firstName: "",
        lastName: "",
        email: email,
        token,
        refreshToken,
        creationYear: new Date().getFullYear().toString(),
        phoneNumber: null,
        profilePicture: null,
        stripeAccountId: null,
        stripeOnboardingComplete: false,
        roles: []
      };
      
      dispatch(setAuthState({ isAuthenticated: true, user: userData }));
      
      // Fetch latest user details after session restore
      dispatch(fetchUserDetails());
      
      return true;
    }
    
    // Token is expired, try to refresh
    const refreshResponse = await apiClient.post<RefreshResponse>("/auth/refresh", { refreshToken });
    if (refreshResponse?.accessToken) {
      const newToken = refreshResponse.accessToken;
      
      // Save the new token
      localStorage.setItem("auth_token", newToken);
      
      // Parse the new token
      const newDecodedToken = parseJwt(newToken);
      const email = newDecodedToken?.sub || "";
      
      // Create user with refreshed token
      const userData: User = {
        id: 0,
        firstName: "",
        lastName: "",
        email: email,
        token: newToken,
        refreshToken,
        creationYear: new Date().getFullYear().toString(),
        phoneNumber: null,
        profilePicture: null,
        stripeAccountId: null,
        stripeOnboardingComplete: newDecodedToken?.stripeOnboardingComplete || false,
        roles: newDecodedToken?.roles || []
      };
      
      dispatch(setAuthState({ isAuthenticated: true, user: userData }));
      
      // Fetch latest user details after session restore
      dispatch(fetchUserDetails());
      
      return true;
    } else {
      throw new Error("Invalid refresh token response");
    }
  } catch {
    dispatch(logout());
    return false;
  }
};

interface RefreshResponse {
accessToken: string;
refreshToken?: string;
}
interface UserDetailsResponse extends Omit<User, 'token' | 'refreshToken'> {
  stripeOnboardingComplete: boolean; // Ensure this field is expected
}

// Update the fetchUserDetails function to properly merge the data
export const fetchUserDetails = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  try {
    // Use the new interface for type safety
    const userDetails = await apiClient.get<UserDetailsResponse>('/users/me');
    if (userDetails) {
      // Get current tokens from state to preserve them
      const currentState = store.getState().auth;
      const token = currentState.user?.token || '';
      const refreshToken = currentState.user?.refreshToken || '';

      // Merge the user details with existing tokens
      dispatch(updateUserProfile({
        ...userDetails,
        token,
        refreshToken,
        // Explicitly include stripeOnboardingComplete from userDetails
        stripeOnboardingComplete: userDetails.stripeOnboardingComplete,
        // Ensure other optional fields are preserved correctly
        stripeAccountId: userDetails.stripeAccountId || null,
        roles: userDetails.roles || []
      }));
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      dispatch(setAuthError(error.response?.data?.message || 'Failed to fetch user details'));
    } else {
      dispatch(setAuthError('An unexpected error occurred'));
    }
  } finally {
    dispatch(setLoading(false));
  }
};

export default authSlice.reducer;